import { motion } from "framer-motion";
import { Truck, Package, Ship, Snowflake, Zap, Warehouse, ArrowUpRight } from "lucide-react";
import { eyebrowFor } from "@/content/sections";
import { EASE } from "@/lib/motion";

const IMG = {
  ftl: "https://images.unsplash.com/photo-1578857371787-189d8b9eda84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxzZW1pJTIwdHJ1Y2slMjBkcml2ZXIlMjBwb3J0cmFpdCUyMG5pZ2h0fGVufDB8fHx8MTc4NDY1NzM1NHww&ixlib=rb-4.1.0&q=85",
  port: "https://images.unsplash.com/photo-1759272840538-ae4b07214c71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxzaGlwcGluZyUyMGNvbnRhaW5lcnMlMjBpbmR1c3RyaWFsJTIwcG9ydHxlbnwwfHx8fDE3ODQ2NTczMzd8MA&ixlib=rb-4.1.0&q=85",
  wh: "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjByYWNrc3xlbnwwfHx8fDE3ODQ2NTczNTR8MA&ixlib=rb-4.1.0&q=85",
  neon: "https://images.unsplash.com/photo-1550275994-cdc89cd1948f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGxpZ2h0JTIwdHJhaWxzJTIwc3BlZWQlMjBuZW9ufGVufDB8fHx8MTc4NDY1NzMzN3ww&ixlib=rb-4.1.0&q=85",
  hero: "https://images.unsplash.com/photo-1766785368863-f2188a8c8b32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxmcmVpZ2h0JTIwdHJ1Y2slMjBoaWdod2F5JTIwZGFyayUyMG5pZ2h0fGVufDB8fHx8MTc4NDY1NzMzN3ww&ixlib=rb-4.1.0&q=85",
};

const services = [
  { icon: Truck, name: "Full Truckload", tag: "FTL", desc: "Dedicated trailers moving your freight direct, coast to coast, with real-time visibility.", img: IMG.ftl },
  { icon: Package, name: "Less-Than-Truckload", tag: "LTL", desc: "Cost-efficient consolidation for smaller shipments without sacrificing transit time.", img: IMG.neon },
  { icon: Ship, name: "Intermodal", tag: "RAIL + ROAD", desc: "Rail-and-road networks that cut cost and carbon on long-haul lanes.", img: IMG.port },
  { icon: Snowflake, name: "Refrigerated", tag: "REEFER", desc: "Temperature-controlled capacity for perishables, pharma and sensitive cargo.", img: IMG.hero },
  { icon: Zap, name: "Expedited", tag: "HOT SHOT", desc: "Time-critical freight with same-day dispatch and 24/7 monitoring.", img: IMG.neon },
  { icon: Warehouse, name: "Warehousing", tag: "3PL", desc: "Flexible storage, cross-docking and fulfillment integrated with your supply chain.", img: IMG.wh },
];

const card = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: EASE } }),
};

export const Services = () => {
  return (
    <section id="services" className="relative z-10 bg-ink py-28 lg:py-40" data-testid="services">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
        <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-neon">{eyebrowFor("services")}</span>
            <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-none tracking-tight text-white md:text-6xl">
              Every mode.<br />One network.
            </h2>
          </div>
          <p className="max-w-sm text-zinc-400">Six freight solutions engineered for shippers who refuse to wait and carriers who keep moving.</p>
        </div>

        <div className="grid grid-cols-1 border-l border-t border-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.name}
                custom={i}
                variants={card}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                data-testid={`service-card-${s.tag.toLowerCase().replace(/[^a-z]/g, "")}`}
                className="group relative overflow-hidden border-b border-r border-white/10"
              >
                <div className="absolute inset-0">
                  <img src={s.img} alt={s.name} className="h-full w-full object-cover opacity-15 grayscale transition-all duration-700 group-hover:scale-105 group-hover:opacity-30 group-hover:grayscale-0" />
                </div>
                <div className="relative z-10 flex h-full min-h-[300px] flex-col justify-between p-8">
                  <div className="flex items-start justify-between">
                    <Icon size={30} strokeWidth={1.4} className="text-neon" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{s.tag}</span>
                  </div>
                  <div>
                    <div className="mb-3 flex items-center gap-2">
                      <h3 className="font-display text-2xl font-semibold uppercase text-white">{s.name}</h3>
                      <ArrowUpRight size={20} className="-translate-x-2 text-neon opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                    </div>
                    <p className="text-sm leading-relaxed text-zinc-400">{s.desc}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
